import { useRef } from 'react';

import { Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useUpdateEffect } from 'react-use';

import { AlertTypes } from 'constants/enums';
import { onChangeSelectionAlerts, onChangeSelectionTasks } from 'store/clinicalLogger/reducer';

import useAppSelector from './useAppSelector';

const useControlledTableSelection = (type: 'tasks' | 'alerts-medical' | 'alerts-compliance') => {
  const dispatch = useDispatch();
  const headerInputRef = useRef<HTMLInputElement>(null);

  const entityId = () => {
    switch (type) {
      case 'alerts-medical':
      case 'alerts-compliance':
        return 'alert_id';
      default:
        return 'id';
    }
  };

  const selection = useAppSelector((state) => {
    switch (type) {
      case 'tasks':
        return state.clinicalLogger.tasks;
      case 'alerts-medical':
        return state.clinicalLogger.alerts.medical as any[];
      case 'alerts-compliance':
        return state.clinicalLogger.alerts.compliance as any[];
      default:
        return [];
    }
  });

  const setSelection = (data: any[]) => {
    if (type === 'tasks') dispatch(onChangeSelectionTasks(data));
    if (type === 'alerts-compliance')
      dispatch(onChangeSelectionAlerts({ alerts: data, type: AlertTypes.COMPLIANCE }));
    if (type === 'alerts-medical')
      dispatch(onChangeSelectionAlerts({ alerts: data, type: AlertTypes.MEDICAL }));
  };

  const handleSelect = (cell: any) => {
    if (selection?.find((x) => x[entityId()] === cell.row.original[entityId()])) {
      setSelection(selection.filter((x) => x[entityId()] !== cell.row.original[entityId()]));
    } else {
      setSelection([...selection, cell.row.original]);
    }
  };

  const handleSelectAll = (rows: any[]) => {
    if (selection?.length === rows.length) {
      setSelection([]);
    } else {
      setSelection(rows);
    }
  };

  const controlledCol = {
    id: 'selection',
    Header: ({ data }: any) => {
      const checked = selection?.length === data.length && data.length > 0;
      if (headerInputRef.current && !checked)
        headerInputRef.current.indeterminate = selection?.length > 0;
      return (
        data.length > 0 && (
          <Form.Check
            id="table-select-all"
            data-test="table-select-all"
            ref={headerInputRef}
            checked={checked}
            onChange={() => handleSelectAll(data)}
          />
        )
      );
    },
    Cell: (cell: any) => {
      const checked = !!selection?.find((x) => x[entityId()] === cell.row.original[entityId()]);
      return (
        <Form.Check
          type="checkbox"
          className="select-row"
          checked={checked}
          onChange={() => handleSelect(cell)}
        />
      );
    },
    maxWidth: 50,
    disableSortBy: true
  };

  useUpdateEffect(() => {
    setSelection(selection);
  }, [selection]);

  return { controlledCol };
};
export default useControlledTableSelection;
